import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  LinearProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import InlineForm from '../../../common/inline/InlineForm';
import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import { TemplateType } from '../../template/Template.interface';
import { useExportFile } from '../Document.hook';
import { FileDto } from '../Document.interface';

export interface DocumentApprovalExportDialogProps {
  open: boolean;
  file: Pick<FileDto, 'id' | 'version'>;
  onCancel?: () => void;
  onSuccess?: (blob: Blob) => void;
}

const DocumentApprovalExportDialog: React.FC<
  DocumentApprovalExportDialogProps
> = props => {
  const { open, file, onCancel, onSuccess } = props;
  const abortRef = useRef<() => void>();
  const [data, setData] = useState<{ message: string; dateOnly: boolean }>({
    message: '',
    dateOnly: false
  });
  const { isLoading, error, exportFile } = useExportFile(file.id, file.version);

  const handleCancel = () => {
    if (!isLoading) {
      onCancel?.();
    }
  };

  const handleExport = async () => {
    // abort previous request
    abortRef.current?.();
    const { request, result } = exportFile({
      type: TemplateType.DocumentApproval,
      data
    });
    abortRef.current = () => request.abort();
    const blob = await result;
    abortRef.current = undefined;
    if (blob) {
      onSuccess?.(blob);
    }
  };

  // clear state
  useEffect(() => {
    if (!open) {
      setData({ message: '', dateOnly: false });
    }
  }, [open]);

  useEffect(() => {
    return () => abortRef.current?.();
  }, []);

  return (
    <Dialog open={open} onClose={handleCancel}>
      {isLoading && <LinearProgress />}
      <DialogTitle>Document Approval Export</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Export this Document including the Approval information.
        </DialogContentText>
        <InlineFormik
          initialValues={data}
          onSubmit={values => {
            setData(data => ({ ...data, ...values }));
          }}
        >
          {formik => (
            <InlineForm>
              <InlineHtml
                submitOnChange
                name='message'
                helperText='Enter custom message if applicable'
                disabled={isLoading}
              />
              <InlineFormControl>
                <FormControlLabel
                  name='dateOnly'
                  label='Date Only'
                  control={
                    <Checkbox
                      color='primary'
                      disabled={isLoading}
                      checked={data.dateOnly}
                      onChange={(_, checked) => {
                        formik.setFieldValue('dateOnly', checked, true);
                        formik.submitForm();
                      }}
                    />
                  }
                />
                <FormHelperText>
                  Hide the Time from displayed Dates from export.
                </FormHelperText>
              </InlineFormControl>
            </InlineForm>
          )}
        </InlineFormik>
        <Collapse in={!!error} style={{ minHeight: 'unset' }}>
          <Box mt={1}>
            <Alert severity='error'>
              {error?.message || 'An error occurred.'}
            </Alert>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color='primary'
          variant='contained'
          disabled={isLoading}
          onClick={handleExport}
        >
          Export as PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentApprovalExportDialog;
